import { initValidation } from '../../core/validation';
import { fieldMessages, alertText } from '../../core/copy';
import { watchEmail } from '../../core/accountLookup';
import { submitRegistration } from '../../core/submit';
import { verificationStep } from '../../core/emailVerification';
import { registrationFailed, invalidCode } from '../../core/alerts';
import { showLoader } from '../../../modules/loaders';
import PromoCodeApi from '../../../modules/promoCodeApi';
import { openCheckoutWith, startPayment } from './checkout';
import { applyDiscount } from './discount';
import { wrongKeyword } from './messages';

/**
 * The account of the plans modal, and what the chosen access asks on top of it.
 *
 * The details sit under the choices on the first stage, and the fields appear only
 * once the address turns out to be free: an address that already has an account is
 * answered with the way to sign in, because both endpoints below are for guests.
 *
 * Two kinds of access, two endings:
 *
 *   book_owner  posts to register/book-owner with the last word of a page
 *   paid        standard, premium or combo: checkout.js opens a Stripe session
 *
 * The first creates the account and signs its owner in here, so the address is
 * confirmed by code in the same modal, as on a nonpublic forum event, before the
 * done screen. There is no "later": closing the modal still leaves them signed in.
 *
 * Every block that belongs to one of them carries data-plans-for, and entering the
 * stage shows that kind's blocks and disables the others' fields, so what is posted
 * and what is validated is only what is on screen.
 *
 * The form refuses an address that already has an account rather than offering a
 * login: discovering you have one is not what registering for a plan is for, and
 * both endpoints it posts to are for guests.
 */

const FORM = '#registrationForm';
const PAID = ['standard', 'premium', 'combo'];

function kindOf(access) {
    return PAID.includes(access) ? 'paid' : access;
}

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').getAttribute('content');
}

/** Cents as the page writes a yearly price: $149 / year, $119.20 / year. */
function perYear(template, cents) {
    const amount = cents / 100;
    const price = '$' + (Number.isInteger(amount) ? amount : amount.toFixed(2));

    return template.replace(':price', price);
}

/**
 * @param {HTMLElement} modal
 * @param {{go: Function}} stages
 * @param {{onVerifying?: Function}} hooks onVerifying runs as the confirmation code is asked
 */
export function accountStage(modal, stages, { onVerifying } = {}) {
    const form = modal.querySelector(FORM);
    const data = form.dataset;
    const submit = modal.querySelector('#lkPlansSubmit');
    const summary = (name) => modal.querySelector(`[data-plans-summary="${name}"]`);

    const say = (key, fallback) => fieldMessages(form)[key] || fallback;

    let selection = null;
    let emailIsValid = true;

    // The account a cancelled checkout left behind. While it is set, the address
    // is settled and the form under it has nothing left to ask.
    let resuming = false;

    // Whether the fields under the address are on screen. They are drawn hidden
    // and disabled, so nothing is asked of, or validated on, somebody who has
    // not said who they are yet.
    let revealed = false;

    // Where a registered visitor goes from here, set once the account exists.
    let continueTo = null;

    openCheckoutWith(data.stripeKey);

    // ── What is on screen ────────────────────────────────────────────────

    function show(kind) {
        modal.querySelectorAll('[data-plans-for]').forEach((block) => {
            const on = block.dataset.plansFor === kind;

            block.hidden = ! on;

            [block, ...block.querySelectorAll('input, select, textarea')]
                .filter((field) => field.matches('input, select, textarea'))
                .forEach((field) => { field.disabled = ! on; });
        });

        const label = { book_owner: 'labelBookOwner', paid: 'labelPaid' }[kind];

        if (label) {
            submit.textContent = submit.dataset[label];
        }
    }

    // Going back to the choices takes the level's own fields off the table: left
    // enabled, the word a book owner never typed would refuse the form on a stage
    // that is not on screen.
    modal.addEventListener('wizard:stage', (event) => {
        if (event.detail.stage === 'choose') {
            show(null);
        }
    });

    // Every field of the form except the address itself, and the notice that
    // replaces them. Collected once: the form is server-rendered and fixed.
    const emailField = form.querySelector('#email');
    const emailBlock = emailField?.closest('.reg-field');
    const status = form.querySelector('[data-plans-status]');

    const profileBlocks = Array.from(form.querySelectorAll('.reg-field'))
        .filter((block) => block !== emailBlock && block !== status);

    function revealProfile(on) {
        revealed = on;

        profileBlocks.forEach((block) => {
            block.hidden = ! on;

            block.querySelectorAll('input, select, textarea').forEach((field) => {
                // The blocks a level owns are the other stage's to enable.
                if (field.closest('[data-plans-for]')) {
                    return;
                }

                field.disabled = ! on;
            });
        });

        // The captcha waits hidden in the shared block until a door reveals it.
        const captcha = modal.querySelector('#recaptcha');

        if (captcha) {
            captcha.style.display = on ? '' : 'none';
        }
    }

    /**
     * Scrolls the modal to a field that is holding the stage up. With the
     * comparison open the account is far below the fold, and a Continue that
     * only reddens something off screen reads as a button that does nothing.
     */
    function bringIntoView(field) {
        const body = modal.querySelector('.lk-modal-body');

        if (! field || ! body) {
            return;
        }

        const block = field.closest('.reg-field') || field;
        const top = block.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop;

        body.scrollTo({ top: Math.max(top - 24, 0), behavior: 'smooth' });
        field.focus({ preventScroll: true });
    }

    // What the address turned out to be: an account that exists, one that does
    // not, or nothing said yet.
    function showStatus(kind) {
        if (! status) {
            return;
        }

        status.hidden = ! kind;

        status.querySelectorAll('[data-plans-status-for]').forEach((block) => {
            block.hidden = block.dataset.plansStatusFor !== kind;
        });

        // The line asking for an address says what will happen next; once that has
        // happened, the answer above says it better and the line is noise.
        const lede = modal.querySelector('[data-plans-email-lede]');

        if (lede) {
            lede.hidden = Boolean(kind);
        }
    }

    // Above the fields they are about, wherever the form draws them: the answer
    // about the address, and then why the details under it are asked for.
    if (status) {
        form.querySelector('.reg-form')?.prepend(status);

        const privacy = form.querySelector('[data-plans-privacy]');

        if (privacy) {
            status.insertAdjacentElement('afterend', privacy);
        }
    }

    revealProfile(false);

    function fillAll(attribute, value) {
        modal.querySelectorAll(`[${attribute}]`).forEach((element) => {
            element.textContent = value;
        });
    }

    // ── Book owner: the page to read ─────────────────────────────────────

    const renewButton = modal.querySelector('[data-plans-renew]');
    const renewLabel = renewButton.innerHTML;
    const challengeAnswer = form.querySelector('[name="challenge_answer"]');

    /**
     * One request at a time, the button busy until it answers. Each one rewrites the
     * page the session holds, so answers arriving out of order would leave on screen
     * a page the server no longer expects. A request that fails keeps the page in force.
     */
    function renewChallenge() {
        if (renewButton.disabled) {
            return Promise.resolve();
        }

        // Held at its width, so the spinner does not shrink the button under the cursor.
        renewButton.style.minWidth = `${renewButton.offsetWidth}px`;
        renewButton.disabled = true;
        renewButton.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

        return fetch(data.renewChallengeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken() },
            body: JSON.stringify({ book_slug: selection.bookSlug })
        })
            .then((response) => {
                if (! response.ok) {
                    throw new Error(`renew-challenge-word answered ${response.status}`);
                }

                return response.json();
            })
            .then((answer) => {
                fillAll('data-plans-challenge-page', answer.randomPage);

                // A word typed for the previous page answers nothing now.
                challengeAnswer.value = '';
            })
            .catch((error) => console.error(error))
            .finally(() => {
                renewButton.disabled = false;
                renewButton.innerHTML = renewLabel;
                renewButton.style.minWidth = '';
            });
    }

    renewButton.addEventListener('click', renewChallenge);

    // ── Paid: the price and the discount ─────────────────────────────────

    const discountInput = modal.querySelector('#discount_code');
    const discountButton = modal.querySelector('[data-plans-apply-discount]');
    const original = summary('original');
    const promoApi = new PromoCodeApi({ applyUrl: data.discountUrl, csrfToken: csrfToken() });

    function showTotal(cents) {
        summary('total').textContent = perYear(data.perYear, cents);
        original.hidden = cents === selection.price;
        original.textContent = perYear(data.perYear, selection.price);
    }

    function resetDiscount() {
        discountInput.value = '';
        discountInput.disabled = false;
        modal.querySelector('#appliedDiscountCode').value = '';
        modal.querySelector('#discountMessage').innerText = '';
        modal.querySelector('#discountMessageSuccess').innerText = '';
    }

    discountButton.addEventListener('click', () => applyDiscount({
        promoApi,
        plan: selection.access === 'combo' ? 'premium' : selection.access,
        bookSlug: selection.bookSlug,
        basePrice: selection.price,
        code: discountInput.value.trim(),
        button: discountButton,
        messages: { applied: data.discountApplied, refused: data.discountRefused },
        onApplied: showTotal
    }));

    // ── Checking, before anything is sent ────────────────────────────────

    const validator = initValidation(FORM, {
        // Disabled unless the book owner block is on screen, and jQuery Validate
        // skips a disabled field, so only the book owner is asked.
        rules: { challenge_answer: { required: true } },
        messages: {
            challenge_answer: {
                required: say('ownership_verification', 'Please provide the answer to verify your ownership.')
            }
        }
    });

    const lookup = watchEmail(form, {
        onBlur: true,
        onExisting: () => {
            emailIsValid = false;
            revealProfile(false);
            showStatus('found');
        },
        onNew: () => {
            emailIsValid = true;
            showStatus('new');
            revealProfile(true);
        },
        onReset: () => {
            emailIsValid = true;
            showStatus(null);
            revealProfile(false);
        }
    });

    // ── Sending, then confirming the address ─────────────────────────────

    /** The done screen's next steps, by the book that was registered. */
    function prepareDone() {
        modal.querySelectorAll('[data-plans-done-book]').forEach((actions) => {
            actions.hidden = actions.dataset.plansDoneBook !== selection.product;
        });
    }

    const verification = verificationStep({
        form: FORM,
        translate: alertText,
        onConfirmed: () => {
            // The code screen is a sibling of the stages, not one of them, so
            // going to the done stage does not take it off the screen. The footer
            // goes with it: the done screen carries its own way on.
            modal.querySelector('#verificationCodeSection').style.display = 'none';
            modal.querySelector('.lk-modal-foot').hidden = true;

            prepareDone();
            stages.go('done');
        },
        onInvalidCode: invalidCode
    });

    /**
     * The account exists and its owner is signed in. The book owner's endpoint
     * renewed the session, so the token the page holds is replaced before the
     * confirmation calls use it.
     */
    function registered(response) {
        if (response.csrf_token) {
            document.querySelector('meta[name="csrf-token"]').setAttribute('content', response.csrf_token);
            form.querySelector('input[name="_token"]').value = response.csrf_token;
        }

        // Where closing the modal takes them; the done screen links on from there.
        continueTo = kindOf(selection.access) === 'book_owner' ? response.redirect_url : data.dashboardUrl;

        // The word may have proven the previous edition, which is the book this
        // account now owns, so the done screen takes its name from the answer.
        if (response.library) {
            fillAll('data-plans-library', response.library);
        }

        // Nothing is left to go back to or submit.
        modal.querySelectorAll('[data-wizard-foot="confirm"]').forEach((control) => { control.hidden = true; });

        // The account exists, so closing loses nothing from here on. Unwatched, or the
        // confirmation code typed next would mark the modal dirty again and closing
        // the done screen would ask to discard changes.
        modal.querySelector('[data-lk-dirty-watch]')?.removeAttribute('data-lk-dirty-watch');

        if (window.LkModal) {
            LkModal.setDirty(modal.id, false);
        }

        verification.begin({
            userId: response.user_id,
            address: form.querySelector('#email').value
        });

        if (onVerifying) {
            onVerifying();
        }
    }

    const registration = submitRegistration({
        form: FORM,
        onAccepted: registered,
        onFailed: (message, response) => {
            const errors = response.errors || {};

            if (errors.challenge_answer) {
                if (response.challenge_page) {
                    fillAll('data-plans-challenge-page', response.challenge_page);
                }

                wrongKeyword(errors.challenge_answer[0]);

                return;
            }

            // A refusal from validation names the field; the sentence is what helps.
            const first = Object.values(errors)[0];

            registrationFailed({ message: first ? first[0] : message });
        }
    });

    submit.addEventListener('click', (event) => {
        event.preventDefault();

        if (! emailIsValid) {
            validator.showErrors({ email: say('email_exists', 'This email address is already registered in the system.') });

            return;
        }

        if (! registration.valid()) {
            return;
        }

        if (kindOf(selection.access) === 'paid') {
            showLoader();
            startPayment({
                subscriptionType: selection.access === 'combo' ? 'premium' : selection.access,
                bookSlug: selection.bookSlug,
                // The account is the one the first checkout made, and the server
                // reads it from the session: the form has nothing left to send.
                resume: resuming
            });

            return;
        }

        registration.send();
    });

    // Registered and signed in, confirmed or not, so the guest page behind is no
    // longer this visitor's.
    modal.addEventListener('lk:close', () => {
        if (continueTo) {
            window.location.href = continueTo;
        }
    });

    return {
        /**
         * Whether the choices stage can hand over: the address has been looked up
         * and is free, and the form under it is filled in. The lookup is asked for
         * here too, because an address filled in by a password manager may never
         * have been left.
         *
         * @returns {Promise<boolean>}
         */
        ready() {
            // The account already exists and its details came from it, so there is
            // no address to look up and no form to pass.
            if (resuming) {
                return Promise.resolve(true);
            }

            const asked = revealed;

            return Promise.resolve(lookup.check()).then(() => {
                if (! emailIsValid) {
                    bringIntoView(emailField);

                    return false;
                }

                // Nothing was asked yet: the address is fine but its answer has not
                // come back, so the fields are still hidden.
                if (! revealed) {
                    validator.showErrors({
                        email: say('email_required', 'Please enter your email address.')
                    });
                    bringIntoView(emailField);

                    return false;
                }

                // The fields appeared on this very click. Marking them all red for
                // being empty reads as a refusal of something nobody was shown yet.
                if (! asked) {
                    bringIntoView(emailField);

                    return false;
                }

                const valid = registration.valid();

                if (! valid) {
                    bringIntoView(form.querySelector('.is-invalid') || emailField);
                }

                return valid;
            });
        },

        /**
         * The purchase this browser left at the checkout. The account is already
         * made, so the address and the details under it are settled and the only
         * thing still open is which plan to buy.
         */
        resume(pending) {
            resuming = true;
            emailIsValid = true;

            revealProfile(false);
            showStatus('resume');

            if (emailBlock) {
                emailBlock.hidden = true;
            }

            if (emailField) {
                // Closing the modal resets its forms, and a reset restores the
                // attribute rather than the property: without both, reopening the
                // modal would carry on with no address and confirm a code to nobody.
                emailField.value = pending.email || '';
                emailField.setAttribute('value', pending.email || '');
                emailField.disabled = true;
            }
        },

        /** Back to an untouched form, for the next time the modal opens. */
        reset() {
            showStatus(resuming ? 'resume' : null);
            revealProfile(false);
            emailIsValid = true;
            modal.querySelector('.lk-modal-foot').hidden = false;
        },

        /**
         * @param {{product: string, access: string, bookSlug: string, bookTitle: string, accessLabel: string, content: string, price: number}} chosen
         */
        enter(chosen) {
            selection = chosen;

            const kind = kindOf(chosen.access);

            show(kind);
            // The only kind that posts this form; a paid plan leaves for Stripe.
            form.action = data.bookOwnerAction;
            form.querySelector('input[name="book_slug"]').value = chosen.bookSlug;
            fillAll('data-plans-book-title', chosen.bookTitle);
            fillAll('data-plans-library', chosen.content);

            modal.querySelectorAll('[data-plans-book-cover]').forEach((cover) => {
                cover.hidden = cover.dataset.plansBookCover !== chosen.library;
            });
            modal.querySelectorAll('[data-plans-plan-cover]').forEach((cover) => {
                cover.hidden = cover.dataset.plansPlanCover !== chosen.library;
            });

            if (kind === 'book_owner') {
                fillAll('data-plans-challenge-page', '');
                renewChallenge();
            }

            if (kind === 'paid') {
                resetDiscount();
                summary('access').textContent = chosen.accessLabel;
                summary('content').textContent = chosen.content;
                showTotal(chosen.price);
            }

            stages.go('confirm');
        }
    };
}
